import { splitNarratorName } from './narratorName';

export interface NarratorSortItem {
  name: string;
  is_system?: unknown;
  nsfw?: unknown;
  is_nsfw?: unknown;
  requiresAdult?: unknown;
  requires_adult?: unknown;
  min_age_level?: unknown;
  minAgeLevel?: unknown;
  required_age_level?: unknown;
  requiredAgeLevel?: unknown;
  age_level?: unknown;
  ageLevel?: unknown;
  type_data?: unknown;
}

const EXPLICIT_NARRATOR_BASE_NAMES = new Set(['maxima', 'begierde', 'verlangen', 'inferno']);
const BOOLEAN_EXPLICIT_KEYS = ['nsfw', 'is_nsfw', 'requiresAdult', 'requires_adult'] as const;
const AGE_LEVEL_KEYS = [
  'min_age_level',
  'minAgeLevel',
  'required_age_level',
  'requiredAgeLevel',
  'age_level',
  'ageLevel',
] as const;
const EXPLICIT_AGE_LEVELS = new Set(['age_16', 'age_18']);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasOwn(record: Record<string, unknown>, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(record, key);
}

function readBoolean(value: unknown): boolean | null {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') {
    if (Number.isFinite(value)) return value !== 0;
    return null;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLocaleLowerCase();
    if (normalized === '1' || normalized === 'true' || normalized === 'yes') return true;
    if (normalized === '0' || normalized === 'false' || normalized === 'no' || normalized === '') return false;
    const parsed = Number(normalized);
    if (Number.isFinite(parsed)) return parsed !== 0;
  }
  if (value === null) return false;
  return null;
}

function readExplicitAge(value: unknown): boolean | null {
  if (typeof value === 'string') {
    const normalized = value.trim().toLocaleLowerCase();
    if (!normalized) return false;
    if (EXPLICIT_AGE_LEVELS.has(normalized)) return true;
    const match = /^age_(\d+)$/.exec(normalized);
    if (match) return Number(match[1]) >= 16;
    const parsed = Number(normalized);
    if (Number.isFinite(parsed)) return parsed >= 16;
    return null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) return value >= 16;
  if (value === null) return false;
  return null;
}

function explicitStatusFromRecord(record: Record<string, unknown>): boolean | null {
  for (const key of BOOLEAN_EXPLICIT_KEYS) {
    if (!hasOwn(record, key)) continue;
    const explicit = readBoolean(record[key]);
    if (explicit !== null) return explicit;
  }

  for (const key of AGE_LEVEL_KEYS) {
    if (!hasOwn(record, key)) continue;
    const explicit = readExplicitAge(record[key]);
    if (explicit !== null) return explicit;
  }

  return null;
}

function explicitStatusFromFields(item: NarratorSortItem): boolean | null {
  const direct = explicitStatusFromRecord(item as unknown as Record<string, unknown>);
  if (direct !== null) return direct;
  return isRecord(item.type_data) ? explicitStatusFromRecord(item.type_data) : null;
}

function normalizeName(value: string): string {
  return value.trim().toLocaleLowerCase('de-DE');
}

export function narratorDisplayName(item: Pick<NarratorSortItem, 'name'>): string {
  return splitNarratorName(item.name).name || item.name.trim();
}

export function isSystemNarrator(item: Pick<NarratorSortItem, 'is_system'>): boolean {
  return readBoolean(item.is_system) === true;
}

export function isExplicitNsfwSystemNarrator(item: NarratorSortItem): boolean {
  if (!isSystemNarrator(item)) return false;

  const explicitFromFields = explicitStatusFromFields(item);
  if (explicitFromFields !== null) return explicitFromFields;

  return EXPLICIT_NARRATOR_BASE_NAMES.has(normalizeName(narratorDisplayName(item)));
}

function narratorSortRank(item: NarratorSortItem): number {
  if (!isSystemNarrator(item)) return 0;
  return isExplicitNsfwSystemNarrator(item) ? 2 : 1;
}

export function compareNarrators<T extends NarratorSortItem>(a: T, b: T): number {
  const rankDelta = narratorSortRank(a) - narratorSortRank(b);
  if (rankDelta !== 0) return rankDelta;

  const displayDelta = narratorDisplayName(a).localeCompare(narratorDisplayName(b), ['de', 'en'], {
    numeric: true,
    sensitivity: 'base',
  });
  if (displayDelta !== 0) return displayDelta;

  return a.name.localeCompare(b.name, ['de', 'en'], { numeric: true, sensitivity: 'base' });
}

export function sortNarrators<T extends NarratorSortItem>(items: readonly T[]): T[] {
  return [...items].sort(compareNarrators);
}
